import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

type ContactFormData = {
  name: string;
  email: string;
  phone: string;
  wechat: string;
  message: string;
};

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    wechat: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isThrottled, setIsThrottled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Handle form submission here

    const apiUrl = process.env.REACT_APP_CONTACT_API_URL;
    if (!apiUrl) {
      console.error('REACT_APP_CONTACT_API_URL is not set');
      setSubmitError('Configuration error: contact API URL is not set.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      let throttled = false;
      try {
        const data = await response.json();
        if (typeof data?.throttled === 'boolean') {
          throttled = data.throttled;
        }
      } catch (parseError) {
        console.warn('Failed to parse contact API response as JSON', parseError);
      }

      setIsThrottled(throttled);
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        wechat: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form', error);
      setSubmitError(t('contact.errorMessage') || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('contact.title')}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label={t('contact.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              label={t('contact.email')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('contact.phone')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('contact.wechat')}
              name="wechat"
              value={formData.wechat}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              label={t('contact.message')}
              name="message"
              value={formData.message}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('contact.submitting') : t('contact.submit')}
            </Button>
            {showSuccess && (
              <Box mt={2}>
                <Alert severity="success" onClose={() => setShowSuccess(false)}>
                  {isThrottled ? (
                    <Typography variant="body2">
                      {t('contact.throttledMessage')}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">{t('contact.successMessage')}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {t('contact.successFollowup')}
                      </Typography>
                    </>
                  )}
                </Alert>
              </Box>
            )}
          </Grid>
        </Grid>
      </form>
      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={() => setSubmitError(null)}
      >
        <Alert severity="error" onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ContactForm; 